'use client';
import React, { useState } from 'react';
import { CheckCircle, Clock, Circle, ChevronDown, ChevronUp, Shield, Mic, Users, FileSearch, Star, ArrowRight } from 'lucide-react';
import Link from 'next/link';

interface VerificationStep {
  id: string;
  number: number;
  title: string;
  description: string;
  status: 'completed' | 'in_progress' | 'pending';
  icon: React.ReactNode;
  completedAt?: string;
  note?: string;
}

const steps: VerificationStep[] = [
  {
    id: 'application',
    number: 1,
    title: 'Application Submitted',
    description: 'Your application has been received and is being reviewed.',
    status: 'completed',
    icon: <FileSearch size={16} />,
    completedAt: '12 Aug 2026',
  },
  {
    id: 'identity',
    number: 2,
    title: 'Identity Verification',
    description: 'We verify your South African ID number against the Home Affairs database.',
    status: 'completed',
    icon: <Shield size={16} />,
    completedAt: '13 Aug 2026',
  },
  {
    id: 'interview',
    number: 3,
    title: '30-Min Work-Readiness Interview',
    description: 'A structured conversation with a Handlenders assessor measuring 5 key dimensions.',
    status: 'in_progress',
    icon: <Mic size={16} />,
    note: 'Interview scheduled for 28 Aug 2026 at 10:00',
  },
  {
    id: 'reference',
    number: 4,
    title: 'Reference Check',
    description: 'We contact at least one professional or personal reference to validate your background.',
    status: 'pending',
    icon: <Users size={16} />,
  },
  {
    id: 'profile',
    number: 5,
    title: 'Work-Readiness Profile Published',
    description: 'Your verified profile goes live in the Handlenders candidate directory.',
    status: 'pending',
    icon: <Star size={16} />,
  },
];

const statusConfig = {
  completed: {
    label: 'Completed',
    bg: 'var(--off-white)',
    color: 'var(--charcoal)',
    iconBg: 'var(--charcoal)',
    iconColor: 'var(--amber)',
  },
  in_progress: {
    label: 'In Progress',
    bg: '#fdf9e3',
    color: 'var(--charcoal)',
    iconBg: 'var(--amber)',
    iconColor: 'var(--charcoal)',
  },
  pending: {
    label: 'Pending',
    bg: 'transparent',
    color: 'var(--muted-foreground)',
    iconBg: 'var(--muted)',
    iconColor: 'var(--muted-foreground)',
  },
};

export default function WorkerVerificationTracker() {
  const [expanded, setExpanded] = useState(false);

  const completedCount = steps.filter((s) => s.status === 'completed').length;
  const currentStep = steps.find((s) => s.status === 'in_progress');
  const progressPct = Math.round((completedCount / steps.length) * 100);

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Shield size={16} style={{ color: 'var(--charcoal)' }} />
              <h3 className="font-bold text-sm" style={{ color: 'var(--charcoal)' }}>Verification Progress</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              {completedCount} of {steps.length} steps complete
              {currentStep && <> · <span style={{ color: 'var(--charcoal)' }}>Next: {currentStep.title}</span></>}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-lg font-bold tabular-nums" style={{ color: 'var(--charcoal)' }}>{progressPct}%</div>
            <div className="text-xs text-muted-foreground">complete</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-2 rounded-full" style={{ background: 'var(--muted)' }}>
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{ background: 'var(--charcoal)', width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="px-5 py-4">
        <div className="space-y-3">
          {(expanded ? steps : steps.slice(0, 3)).map((step, i) => {
            const cfg = statusConfig[step.status];
            return (
              <div
                key={step.id}
                className="flex items-start gap-3 p-3 rounded-xl transition-colors"
                style={{ background: cfg.bg }}
              >
                {/* Step icon */}
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: cfg.iconBg, color: cfg.iconColor }}
                >
                  {step.status === 'completed' ? (
                    <CheckCircle size={14} />
                  ) : step.status === 'in_progress' ? (
                    <Clock size={14} />
                  ) : (
                    <Circle size={14} />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold" style={{ color: cfg.color }}>{step.title}</span>
                    {step.status === 'completed' && step.completedAt && (
                      <span className="text-xs flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>{step.completedAt}</span>
                    )}
                    {step.status === 'in_progress' && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0" style={{ background: 'var(--amber)', color: 'var(--charcoal)' }}>
                        Active
                      </span>
                    )}
                  </div>
                  {step.status !== 'pending' && (
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{step.description}</p>
                  )}
                  {step.note && (
                    <p className="text-xs mt-1 font-medium" style={{ color: 'var(--charcoal)' }}>📅 {step.note}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {steps.length > 3 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-xl transition-colors hover:bg-muted/50"
            style={{ color: 'var(--muted-foreground)' }}
          >
            {expanded ? (
              <><ChevronUp size={14} />Show less</>
            ) : (
              <><ChevronDown size={14} />Show all {steps.length} steps</>
            )}
          </button>
        )}
      </div>

      {/* Footer CTA */}
      <div className="px-5 pb-5">
        <Link
          href="/candidate#verify"
          className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-xs font-bold transition-all active:scale-95"
          style={{ background: 'var(--charcoal)', color: 'var(--amber)' }}
        >
          <span>View full verification details</span>
          <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
